import { useMemo } from 'react';
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbOverrideMap } from './BreadcrumbOverrideContext';
import { getModuleByPath, getNavigationLabelForPath, hasModuleRoute } from '@core/router/moduleRegistry';
import { listReturnUrl } from '@shared/utils/listReturn';

/**
 * BreadcrumbItem represents a single item in the breadcrumb trail
 */
interface BreadcrumbItem {
  label: string;
  path?: string;
  isLast: boolean;
}

/** URL segments of "create" pages (e.g. /korisnici/novi) */
const NEW_SEGMENTS = ['novi', 'nova', 'novo'];

/** Whether a URL segment is an entity id (GUID or number) */
const isIdSegment = (segment: string | undefined) =>
  !!segment &&
  (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) || /^\d+$/.test(segment));

/**
 * Route label mappings for breadcrumb display
 * Maps URL segments to translation keys
 */
const routeLabelMap: Record<string, string> = {
  dashboard: 'nav.dashboard',
  fleets: 'modules.fleet.name',
  vozila: 'fleets.vehicles.title',
  servisi: 'fleets.services.title',
  ugovori: 'fleets.contracts.title',
  'vulkanizerski-troskovi': 'fleets.tireProcurement.title',
  voznje: 'fleets.trips.title', // Vožnje (Trips)
  'putni-nalozi': 'fleets.travelOrders.title', // Putni nalozi (Travel Order Periods)
  izvestaji: 'fleets.reports.title',
  novi: 'common.new',
  nova: 'common.new',
  izmeni: 'common.edit',
  zahtevi: 'fleets.requests.title',
  users: 'modules.users.name',
  lista: 'common.list',
  uloge: 'users.roles.title',
  permisije: 'users.permissions.title',
  'nalozi-zaposlenih': 'users.employeeAccounts.title',
  roles: 'users.roles.title',
  permissions: 'users.permissions.title',
  registracije: 'fleets.registrations.title',
  'vozni-park': 'modules.fleet.name',
  dobavljaci: 'fleets.providers.title',
  gorivo: 'fleets.fuelLogs.title',
  gume: 'fleets.nav.tireTracking',
  'radni-nalozi': 'fleets.workOrders.title',
  korisnici: 'fleets.fleetUsers.list.title',
  'organizacione-jedinice': 'fleets.operationalUnits.title',
  profil: 'account.title',
  servis: 'fleets.services.title',
};

/**
 * Breadcrumb Component
 *
 * Displays the current navigation path based on the URL.
 * 
 * Features:
 * - Automatic path parsing
 * - Translated labels
 * - Clickable navigation, only for segments that have a page (others are plain text)
 * - ID handling (human-readable label registered by the page, shortened ID as fallback)
 */
export const Breadcrumb = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const overrides = useBreadcrumbOverrideMap();

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    if (pathSegments.length === 0) {
      return [];
    }

    const items: BreadcrumbItem[] = [];

    let currentPath = '';
    // Link only to paths a route renders (e.g. /korisnici/:id has no page, only /korisnici/:id/izmeni)
    const linkTo = (path: string, isLast: boolean) => (isLast || !hasModuleRoute(path) ? undefined : path);
    
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;
      
      if (isIdSegment(segment)) {
        // Prefer a human-readable override registered by the detail/edit page
        // (e.g. vehicle internalCode, workOrderNumber, provider name).
        // Fall back to a shortened GUID while data is still loading.
        const override = overrides[segment];
        const fallback = segment.length > 8 ? `${segment.substring(0, 8)}...` : segment;
        items.push({
          label: override ?? fallback,
          path: linkTo(currentPath, isLast),
          isLast,
        });
      } else {
        // Label from our map, then from the module's sidebar navigation, then the segment itself
        const labelKey = routeLabelMap[segment.toLowerCase()];
        const navItem = labelKey ? undefined : getNavigationLabelForPath(currentPath);
        const label = labelKey
          ? t(labelKey, segment)
          : navItem
            ? (navItem.labelKey ? t(navItem.labelKey, navItem.label) : navItem.label)
            : segment;
        
        items.push({
          label,
          path: linkTo(currentPath, isLast),
          isLast,
        });
      }
    });

    // The first segment is the module base (e.g. "vozni-park"); the home link stands in for it,
    // so it is dropped. When the URL has no section segment after it (e.g. /korisnici/:id/izmeni,
    // /korisnici/novi, /korisnici/lista), the module name is used so the trail still leads back
    // to the module.
    const module = getModuleByPath(location.pathname);
    const moduleName = module?.moduleInfo ? t(module.moduleInfo.labelKey) : undefined;
    if (module && moduleName && `/${pathSegments[0]}` === module.basePath && items.length > 1) {
      if (isIdSegment(pathSegments[1]) || NEW_SEGMENTS.includes(pathSegments[1])) {
        items[0] = { label: moduleName, path: linkTo(module.basePath, false), isLast: false };
        return items;
      }
      if (pathSegments[1] === 'lista') {
        items[1] = { ...items[1], label: moduleName };
      }
    }

    return items.slice(1);
  }, [location.pathname, t, overrides]);

  // Don't render breadcrumbs for root/dashboard
  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    // Plain text on the page background (no box), aligned with the content card below
    <Box sx={{ px: 0.5 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-li': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {/* Home link */}
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
          }}
        >
          <HomeIcon sx={{ fontSize: 18, mr: 0.5 }} />
          <Typography variant="body2">{t('nav.dashboard')}</Typography>
        </Link>

        {/* Path segments */}
        {breadcrumbs.map((item, index) => {
          // Current page, or an intermediate segment without its own page: plain text
          if (!item.path) {
            return (
              <Typography
                key={index}
                variant="body2"
                color={item.isLast ? 'text.primary' : 'text.secondary'}
                fontWeight={item.isLast ? 500 : 400}
              >
                {item.label}
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              component={RouterLink}
              // A list opens with the filters last used in this tab
              to={listReturnUrl(item.path!)}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
              }}
            >
              <Typography variant="body2">{item.label}</Typography>
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};
